import { useEffect, useRef, useState, useCallback } from 'react'
import type { Chat } from '../types'
import { chatTitle as chatTitleOf } from '../lib/displayName'

const SYSTEM_ACCOUNT_ID = '00000000-0000-0000-0000-000000000001'

function isSystemChat(chat: Chat | undefined): boolean {
  if (!chat) return false
  return (chat.members ?? []).includes(SYSTEM_ACCOUNT_ID) || chat.peer?.id === SYSTEM_ACCOUNT_ID
}

function SystemChatBanner() {
  return (
    <div className="flex items-center justify-center gap-2 px-4 py-3 border-t border-divider bg-bg-secondary text-txt-secondary text-sm">
      <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
        <path d="M12 22s8-4 8-10V5l-8-3-8 3v7c0 6 8 10 8 10z"/>
      </svg>
      Это служебный чат. Отправка сообщений недоступна.
    </div>
  )
}
import { useAuthStore } from '../stores/authStore'
import { useChatStore } from '../stores/chatStore'
import { chatsApi } from '../api/chats'
import { callsApi } from '../api/calls'
import { usersApi } from '../api/users'
import { useUiStore } from '../stores/uiStore'
import { useCallStore } from '../stores/callStore'
import { useWebSocket } from '../hooks/useWebSocket'
import { useCallsSocket } from '../hooks/useCallsSocket'
import { ChatList } from '../components/chat/ChatList'
import { MessageList } from '../components/chat/MessageList'
import { MessageInput } from '../components/chat/MessageInput'
import { Avatar } from '../components/ui/Avatar'
import { UserProfileModal } from '../components/chat/UserProfileModal'
import { CallModal } from '../components/call/CallModal'
import { GroupCallModal } from '../components/call/GroupCallModal'
import { IncomingCallModal } from '../components/call/IncomingCallModal'
import { startGroupCall } from '../lib/groupCall'
import { ChatSearchBar } from '../components/chat/ChatSearchBar'
import { ChatInfoPanel } from '../components/chat/ChatInfoPanel'
import type { User } from '../types'
import type { IncomingCall } from '../stores/callStore'

interface ActiveCall {
  peer: User
  callType: 'voice' | 'video'
  mode: 'caller' | 'callee'
  roomId?: string
}

export function AppPage() {
  const { accessToken } = useAuthStore()
  const { activeChatId, chats, setActiveChat } = useChatStore()
  const { wallpaper, chatWallpapers } = useUiStore()
  const incomingCall = useCallStore((s) => s.incoming)
  const setIncoming = useCallStore((s) => s.setIncoming)
  const groupCall = useCallStore((s) => s.groupCall)
  const setGroupCall = useCallStore((s) => s.setGroupCall)
  const [showProfile, setShowProfile] = useState(false)
  const [activeCall, setActiveCall] = useState<ActiveCall | null>(null)
  const [showChatSearch, setShowChatSearch] = useState(false)
  const [showInfo, setShowInfo] = useState(false)
  const [webAppUrl, setWebAppUrl] = useState<string | null>(null)
  useWebSocket(accessToken)
  useCallsSocket(accessToken)

  const activeChat = chats.find((c) => c.id === activeChatId)
  const chatTitle = chatTitleOf(activeChat)
  const peerOnline = !!activeChat?.peer?.online
  const chatStatus = (() => {
    if (!activeChat) return ''
    if (peerOnline) return 'в сети'
    if (activeChat.peer?.last_seen_at) {
      const d = new Date(activeChat.peer.last_seen_at)
      const now = new Date()
      const today = new Date(now.getFullYear(), now.getMonth(), now.getDate())
      const yesterday = new Date(today.getTime() - 86_400_000)
      const t = d.toLocaleTimeString('ru-RU', { hour: '2-digit', minute: '2-digit' })
      if (now.getTime() - d.getTime() < 60_000) return 'заходил(а) только что'
      if (d >= today) return `заходил(а) в ${t}`
      if (d >= yesterday) return `заходил(а) вчера в ${t}`
      return `заходил(а) ${d.toLocaleDateString('ru-RU', { day: 'numeric', month: 'long' })}`
    }
    if (activeChat.type === 'group') {
      const count = activeChat.members?.length
      return count ? `${count} участников` : 'группа'
    }
    return ''
  })()

  const prevChatIdRef = useRef<string | null | undefined>(undefined)

  // Синхронизация activeChatId → URL + history
  // Первое открытие чата (null→id) создаёт history entry (pushState), чтобы кнопка
  // Back возвращала на главную. Переключение между чатами — replaceState (Back сразу на
  // главную, не прыгает по цепочке чатов). Закрытие чата — replaceState на URL без chat.
  useEffect(() => {
    const prev = prevChatIdRef.current
    prevChatIdRef.current = activeChatId

    if (prev === undefined) return // первый рендер — не трогаем URL, он уже правильный

    const url = new URL(window.location.href)
    if (activeChatId) {
      url.searchParams.set('chat', activeChatId)
      if (prev === null) {
        // home → chat: создаём history entry для кнопки Back
        window.history.pushState({ chat: activeChatId }, '', url.toString())
      } else {
        // chat → другой chat: заменяем (Back всегда ведёт на главную)
        window.history.replaceState({ chat: activeChatId }, '', url.toString())
      }
    } else {
      url.searchParams.delete('chat')
      window.history.replaceState({}, '', url.toString())
    }
  }, [activeChatId])

  // Восстановление чата из URL при монтировании и при Back/Forward
  useEffect(() => {
    const syncFromUrl = () => {
      const chatId = new URLSearchParams(window.location.search).get('chat')
      setActiveChat(chatId ?? null)
    }
    syncFromUrl()  // на mount читаем ?chat= из URL
    window.addEventListener('popstate', syncFromUrl)
    return () => window.removeEventListener('popstate', syncFromUrl)
  }, [setActiveChat])

  // Прямой ответ на звонок из пуша: резолвим звонящего и сразу открываем звонок
  // (без второго подтверждения в модалке).
  const answerCall = useCallback((roomId: string, callerId: string, callType: 'voice' | 'video') => {
    window.focus()
    usersApi.getProfile(callerId)
      .then((caller) => {
        setIncoming(null)
        setActiveCall({ peer: caller, callType, mode: 'callee', roomId })
      })
      .catch(() => setIncoming({ roomId, callerId, callType }))
  }, [setIncoming])

  // Сообщения от Service Worker по клику на пуш / кнопкам в нём.
  useEffect(() => {
    if (!('serviceWorker' in navigator)) return
    const handler = (event: MessageEvent) => {
      const data = event.data as {
        type?: string; chatId?: string
        roomId?: string; callerId?: string; callType?: string
      } | undefined
      if (!data?.type) return
      if (data.type === 'OPEN_CHAT' && data.chatId) {
        setActiveChat(data.chatId)
        window.focus()
      } else if (data.type === 'MARK_READ' && data.chatId) {
        const chatId = data.chatId
        const last = useChatStore.getState().chats.find((c) => c.id === chatId)?.last_message?.id
        if (last) chatsApi.markRead(chatId, last).catch(() => {})
        useChatStore.getState().resetUnread(chatId)
      } else if (data.type === 'CALL_ANSWER' && data.roomId) {
        // «Ответить» в пуше — сразу принимаем звонок (без второго подтверждения).
        answerCall(data.roomId, data.callerId ?? '', data.callType === 'video' ? 'video' : 'voice')
      } else if (data.type === 'CALL_DECLINE' && data.roomId) {
        callsApi.reject(data.roomId).catch(() => {})
        setIncoming(null)
      }
    }
    navigator.serviceWorker.addEventListener('message', handler)
    return () => navigator.serviceWorker.removeEventListener('message', handler)
  }, [setActiveChat, setIncoming, answerCall])

  // Открытие звонка из пуша (новая вкладка): ?call=roomId&from=&ctype=
  useEffect(() => {
    const params = new URLSearchParams(window.location.search)
    const roomId = params.get('call')
    if (!roomId) return
    answerCall(roomId, params.get('from') ?? '', params.get('ctype') === 'video' ? 'video' : 'voice')
    params.delete('call'); params.delete('from'); params.delete('ctype')
    const qs = params.toString()
    window.history.replaceState({}, '', window.location.pathname + (qs ? '?' + qs : ''))
  }, [answerCall])

  // Close search/info when chat changes
  useEffect(() => {
    setShowChatSearch(false)
    setShowInfo(false)
  }, [activeChatId])

  // Ctrl+F — chat search
  useEffect(() => {
    const handler = (e: KeyboardEvent) => {
      if ((e.ctrlKey || e.metaKey) && e.key === 'f' && activeChatId) {
        e.preventDefault()
        setShowChatSearch((s) => !s)
      }
      if (e.key === 'Escape') {
        setShowChatSearch(false)
        setShowInfo(false)
      }
    }
    window.addEventListener('keydown', handler)
    return () => window.removeEventListener('keydown', handler)
  }, [activeChatId])

  return (
    <div className="flex h-full bg-bg-secondary overflow-hidden">
      {/* Sidebar */}
      <aside
        className={`
          flex-col shrink-0 border-r border-divider bg-bg-primary overflow-hidden
          md:flex md:w-60 lg:w-80
          ${activeChatId ? 'hidden' : 'flex w-full'}
        `}
      >
        <div className="flex-1 overflow-hidden flex flex-col min-h-0">
          <ChatList />
        </div>
      </aside>

      {/* Main chat area */}
      <main
        className={`
          flex-col min-w-0
          md:flex md:flex-1
          ${activeChatId ? 'flex flex-1' : 'hidden'}
        `}
      >
        {activeChatId && activeChat ? (
          <>
            {/* Chat header — Telegram style */}
            <header className="flex items-center gap-2 px-3 py-2 bg-bg-primary border-b border-divider shrink-0 min-h-[56px]">
              {/* Mobile: back button */}
              <button
                onClick={() => setActiveChat(null)}
                className="md:hidden shrink-0 w-9 h-9 flex items-center justify-center text-accent hover:bg-bg-hover rounded-full transition-colors"
                aria-label="Back"
              >
                <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
                  <polyline points="15 18 9 12 15 6" />
                </svg>
              </button>

              {/* Avatar + name — click opens info */}
              <button
                className="flex items-center gap-2.5 flex-1 min-w-0 text-left"
                onClick={() => setShowInfo((s) => !s)}
              >
                <Avatar
                  name={chatTitle}
                  src={activeChat.avatar_url ?? activeChat.peer?.avatar_url}
                  size={36}
                  online={activeChat.peer?.online}
                />
                <div className="flex-1 min-w-0">
                  <p className="text-txt-primary font-semibold text-[15px] truncate leading-tight">{chatTitle}</p>
                  {chatStatus && (
                    <p className={`text-[12px] truncate leading-tight ${peerOnline ? 'text-accent' : 'text-txt-secondary'}`}>
                      {chatStatus}
                    </p>
                  )}
                </div>
              </button>

              {/* Right icons: search + call + kebab */}
              <div className="flex items-center shrink-0">
                {/* Search in chat */}
                <button
                  onClick={() => setShowChatSearch((s) => !s)}
                  className={`w-9 h-9 flex items-center justify-center rounded-full transition-colors
                    ${showChatSearch ? 'text-accent bg-accent/10' : 'text-txt-secondary hover:text-txt-primary hover:bg-bg-hover'}`}
                  title="Поиск (Ctrl+F)"
                >
                  <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                    <circle cx="11" cy="11" r="8"/><line x1="21" y1="21" x2="16.65" y2="16.65"/>
                  </svg>
                </button>
                {/* Voice call (only for private chats) */}
                {activeChat.peer && (
                  <button
                    onClick={() => activeChat.peer && setActiveCall({ peer: activeChat.peer, callType: 'voice', mode: 'caller' })}
                    className="w-9 h-9 flex items-center justify-center rounded-full text-txt-secondary hover:text-txt-primary hover:bg-bg-hover transition-colors"
                    title="Голосовой звонок"
                  >
                    <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                      <path d="M22 16.92v3a2 2 0 0 1-2.18 2 19.79 19.79 0 0 1-8.63-3.07A19.5 19.5 0 0 1 4.69 13 19.79 19.79 0 0 1 1.61 4.48 2 2 0 0 1 3.6 2.28h3a2 2 0 0 1 2 1.72c.127.96.361 1.903.7 2.81a2 2 0 0 1-.45 2.11L7.91 9.91a16 16 0 0 0 6 6l.91-.91a2 2 0 0 1 2.11-.45c.907.339 1.85.573 2.81.7A2 2 0 0 1 21.72 17z"/>
                    </svg>
                  </button>
                )}
                {/* Video call (only for private chats) */}
                {activeChat.peer && (
                  <button
                    onClick={() => activeChat.peer && setActiveCall({ peer: activeChat.peer, callType: 'video', mode: 'caller' })}
                    className="w-9 h-9 flex items-center justify-center rounded-full text-txt-secondary hover:text-txt-primary hover:bg-bg-hover transition-colors"
                    title="Видеозвонок"
                  >
                    <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                      <polygon points="23 7 16 12 23 17 23 7"/>
                      <rect x="1" y="5" width="15" height="14" rx="2" ry="2"/>
                    </svg>
                  </button>
                )}
                {/* Group call buttons (audio + video) */}
                {activeChat.type === 'group' && (
                  <>
                    <button
                      onClick={() => startGroupCall(activeChat.id, 'voice', chatTitle).catch(() => {})}
                      className="w-9 h-9 flex items-center justify-center rounded-full text-txt-secondary hover:text-txt-primary hover:bg-bg-hover transition-colors"
                      title="Групповой голосовой звонок"
                    >
                      <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                        <path d="M22 16.92v3a2 2 0 0 1-2.18 2 19.79 19.79 0 0 1-8.63-3.07A19.5 19.5 0 0 1 4.69 13 19.79 19.79 0 0 1 1.61 4.48 2 2 0 0 1 3.6 2.28h3a2 2 0 0 1 2 1.72c.127.96.361 1.903.7 2.81a2 2 0 0 1-.45 2.11L7.91 9.91a16 16 0 0 0 6 6l.91-.91a2 2 0 0 1 2.11-.45c.907.339 1.85.573 2.81.7A2 2 0 0 1 21.72 17z"/>
                      </svg>
                    </button>
                    <button
                      onClick={() => startGroupCall(activeChat.id, 'video', chatTitle).catch(() => {})}
                      className="w-9 h-9 flex items-center justify-center rounded-full text-txt-secondary hover:text-txt-primary hover:bg-bg-hover transition-colors"
                      title="Групповой видеозвонок"
                    >
                      <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                        <polygon points="23 7 16 12 23 17 23 7"/>
                        <rect x="1" y="5" width="15" height="14" rx="2" ry="2"/>
                      </svg>
                    </button>
                  </>
                )}

                {/* Web App for bots */}
                {activeChat.peer?.is_bot && (activeChat.peer as User & { web_app_url?: string }).web_app_url && (
                  <button
                    onClick={() => setWebAppUrl((activeChat.peer as User & { web_app_url?: string }).web_app_url!)}
                    className="w-9 h-9 flex items-center justify-center rounded-full text-txt-secondary hover:text-txt-primary hover:bg-bg-hover transition-colors"
                    title="Открыть мини-приложение"
                  >
                    <svg width="17" height="17" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                      <rect x="2" y="3" width="20" height="14" rx="2"/><line x1="8" y1="21" x2="16" y2="21"/><line x1="12" y1="17" x2="12" y2="21"/>
                    </svg>
                  </button>
                )}
                {/* Info / kebab */}
                <button
                  onClick={() => setShowInfo((s) => !s)}
                  className={`w-9 h-9 flex items-center justify-center rounded-full transition-colors
                    ${showInfo ? 'text-accent bg-accent/10' : 'text-txt-secondary hover:text-txt-primary hover:bg-bg-hover'}`}
                  title="Информация о чате"
                >
                  <svg width="18" height="18" viewBox="0 0 24 24" fill="currentColor">
                    <circle cx="12" cy="5" r="1.5"/><circle cx="12" cy="12" r="1.5"/><circle cx="12" cy="19" r="1.5"/>
                  </svg>
                </button>
              </div>
            </header>

            {/* Chat search bar */}
            {showChatSearch && (
              <ChatSearchBar
                chatId={activeChatId}
                onClose={() => setShowChatSearch(false)}
              />
            )}

            {/* Messages + Input */}
            <div className="relative flex flex-col flex-1 min-h-0 bg-bg-secondary">
              <div
                className="flex flex-col flex-1 min-w-0 min-h-0"
                style={{ background: (activeChatId && chatWallpapers[activeChatId]) || wallpaper || undefined }}
              >
                <MessageList chatId={activeChatId} />
                {isSystemChat(activeChat)
                  ? <SystemChatBanner />
                  : <MessageInput chatId={activeChatId} />
                }
              </div>
              {/* Info panel: slides over chat without resizing it */}
              {showInfo && (
                <div className="absolute inset-y-0 right-0 w-80 bg-bg-primary border-l border-divider shadow-2xl z-20 overflow-y-auto animate-fade-in">
                  <ChatInfoPanel chatId={activeChatId} onClose={() => setShowInfo(false)} />
                </div>
              )}
            </div>
          </>
        ) : (
          <div className="flex-1 flex flex-col items-center justify-center text-center px-4 bg-bg-secondary">
            <div className="w-28 h-28 rounded-full bg-bg-input flex items-center justify-center mb-5">
              <svg width="56" height="56" viewBox="0 0 24 24" fill="none" className="text-txt-meta" stroke="currentColor" strokeWidth="1.2" strokeLinecap="round" strokeLinejoin="round">
                <path d="M21 15a2 2 0 0 1-2 2H7l-4 4V5a2 2 0 0 1 2-2h14a2 2 0 0 1 2 2z"/>
              </svg>
            </div>
            <h2 className="text-txt-primary text-[22px] font-medium mb-2">Выберите чат</h2>
            <p className="text-txt-secondary text-[14px] max-w-xs leading-relaxed">
              Выберите беседу в списке слева, чтобы начать переписку
            </p>
          </div>
        )}
      </main>

      {/* User profile modal */}
      {showProfile && activeChat?.peer && (
        <UserProfileModal
          user={activeChat.peer}
          onClose={() => setShowProfile(false)}
          onCall={(type) => {
            setShowProfile(false)
            if (activeChat.peer) setActiveCall({ peer: activeChat.peer, callType: type, mode: 'caller' })
          }}
        />
      )}

      {/* Incoming call modal */}
      {incomingCall && !activeCall && (
        <IncomingCallModal
          call={incomingCall}
          onAccept={(call: IncomingCall, caller: User) => {
            setIncoming(null)
            setActiveCall({ peer: caller, callType: call.callType, mode: 'callee', roomId: call.roomId })
          }}
          onReject={() => setIncoming(null)}
        />
      )}

      {/* Active call modal */}
      {activeCall && (
        <CallModal
          peer={activeCall.peer}
          callType={activeCall.callType}
          mode={activeCall.mode}
          roomId={activeCall.roomId}
          onClose={() => setActiveCall(null)}
        />
      )}

      {groupCall && (
        <GroupCallModal
          roomId={groupCall.roomId}
          callType={groupCall.callType}
          title={groupCall.title}
          onClose={() => setGroupCall(null)}
        />
      )}

      {/* Web App (Mini App) overlay */}
      {webAppUrl && (
        <div className="fixed inset-0 z-50 flex flex-col bg-black/60 animate-fade-in">
          <div className="flex flex-col bg-bg-primary rounded-t-2xl mt-auto" style={{ height: '90dvh' }}>
            <div className="flex items-center justify-between px-4 py-3 border-b border-divider shrink-0">
              <span className="text-txt-primary font-medium text-sm">Mini App</span>
              <button
                onClick={() => setWebAppUrl(null)}
                className="text-txt-secondary hover:text-txt-primary transition-colors text-lg leading-none"
              >
                ✕
              </button>
            </div>
            <iframe
              src={webAppUrl}
              className="flex-1 w-full border-0"
              allow="camera; microphone; geolocation"
              title="Мини-приложение"
            />
          </div>
        </div>
      )}
    </div>
  )
}
